import dayjs from 'dayjs';
import { PROGRESS_COLOR, PROGRESS_LABEL, VIEWS } from './trackerConstants';

const formatDateTime = (value) => (value ? dayjs(value).format('DD-MM-YYYY hh:mm A') : '—');

// valueGetter keeps the CSV export a plain label; renderCell only colours it on screen
const pill = (label, { color, bg }) => (
  <span style={{ color, background: bg, padding: '2px 10px', borderRadius: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
    {label}
  </span>
);

const NEW_TUTOR_COLOR = { true: { color: '#065F46', bg: '#D1FAE5' }, false: { color: '#991B1B', bg: '#FEE2E2' } };

// tutor mobiles are built the same way as the CRD Tutor Replacements register (+countrycode mob_no)
const columns = {
  slNo: { field: 'slNo', headerName: 'Sl No', width: 80 },
  admnNo: { field: 'admnNo', headerName: 'Admission No', width: 160, sortable: true },
  studentName: { field: 'studentName', headerName: 'Student Name', width: 200, toolTipGetter: true, sortable: true },
  projectName: { field: 'projectName', headerName: 'Project', width: 150 },
  courseName: { field: 'courseName', headerName: 'Course', width: 240, toolTipGetter: true },
  subjectName: { field: 'subjectName', headerName: 'Subject', width: 180, toolTipGetter: true },
  oldTutorName: { field: 'oldTutorName', headerName: 'Inactivated Tutor', width: 200, toolTipGetter: true },
  oldTutorMobile: { field: 'oldTutorMobile', headerName: 'Inactivated Tutor Mobile', width: 190 },
  oldTutorEmail: { field: 'oldTutorEmail', headerName: 'Inactivated Tutor Email', width: 240, toolTipGetter: true },
  inactivatedOn: {
    field: 'inactivatedOn',
    headerName: 'Inactivated On',
    width: 190,
    sortable: true,
    valueGetter: ({ value }) => formatDateTime(value),
  },
  unmapReason: { field: 'unmapReason', headerName: 'Inactivation Reason', width: 260, toolTipGetter: true },
  newTutorAssigned: {
    field: 'newTutorAssigned',
    headerName: 'New Tutor Status',
    width: 190,
    valueGetter: ({ value }) => (value ? 'New tutor assigned' : 'Awaiting new tutor'),
    renderCell: ({ row, value }) => pill(value, NEW_TUTOR_COLOR[Boolean(row.newTutorAssigned)]),
  },
  newTutorName: { field: 'newTutorName', headerName: 'New Tutor', width: 200, toolTipGetter: true },
  newTutorMobile: { field: 'newTutorMobile', headerName: 'New Tutor Mobile', width: 170 },
  newTutorAssignedOn: {
    field: 'newTutorAssignedOn',
    headerName: 'New Tutor Assigned On',
    width: 200,
    valueGetter: ({ value }) => formatDateTime(value),
  },
  daysWithoutTutor: {
    field: 'daysWithoutTutor',
    headerName: 'Days Without Tutor',
    width: 170,
    sortable: true,
    valueGetter: ({ value }) => (value == null ? '—' : String(value)),
  },
  croName: { field: 'croName', headerName: 'Current CRO', width: 190, toolTipGetter: true, sortable: true },
  assignedToName: { field: 'assignedToName', headerName: 'Assigned To', width: 190, toolTipGetter: true, sortable: true },
  assignedByName: { field: 'assignedByName', headerName: 'Assigned By', width: 180, toolTipGetter: true },
  assignedOn: { field: 'assignedOn', headerName: 'Assigned On', width: 190, valueGetter: ({ value }) => formatDateTime(value) },
  assignRemarks: { field: 'assignRemarks', headerName: 'Assign Remarks', width: 240, toolTipGetter: true },
  progressStatus: {
    field: 'progressStatus',
    headerName: 'Progress',
    width: 150,
    valueGetter: ({ value }) => PROGRESS_LABEL[value ?? 0],
    renderCell: ({ row, value }) => pill(value, PROGRESS_COLOR[row.progressStatus ?? 0]),
  },
  progressRemarks: { field: 'progressRemarks', headerName: 'Progress Remarks', width: 260, toolTipGetter: true },
  progressUpdatedByName: { field: 'progressUpdatedByName', headerName: 'Progress Updated By', width: 190, toolTipGetter: true },
  progressUpdatedOn: {
    field: 'progressUpdatedOn',
    headerName: 'Progress Updated On',
    width: 200,
    sortable: true,
    valueGetter: ({ value }) => formatDateTime(value),
  },
};

const actions = {
  assign: {
    field: 'assign',
    headerName: 'Assign',
    width: 140,
    type: 'action',
    actionType: 'add',
    actionKey: 'assign',
    icon: 'mdi:account-arrow-right-outline',
    buttonName: 'Assign',
  },
  progress: {
    field: 'progress',
    headerName: 'Progress',
    width: 150,
    type: 'action',
    actionType: 'edit',
    actionKey: 'progress',
    buttonName: 'Update',
  },
  history: {
    field: 'history',
    headerName: 'History',
    width: 130,
    type: 'action',
    actionType: 'view',
    actionKey: 'history',
    buttonName: 'History',
    isDisabled: ({ row }) => !row.assignedTo,
  },
};

const pick = (...keys) => keys.map((key) => columns[key]);

const studentColumns = ['slNo', 'admnNo', 'studentName', 'projectName', 'courseName', 'subjectName'];
const tutorColumns = [
  'oldTutorName',
  'oldTutorMobile',
  'oldTutorEmail',
  'inactivatedOn',
  'unmapReason',
  'newTutorAssigned',
  'newTutorName',
  'newTutorMobile',
  'newTutorAssignedOn',
  'daysWithoutTutor',
];
const progressColumns = ['progressStatus', 'progressRemarks', 'progressUpdatedByName', 'progressUpdatedOn'];

export const TRACKER_HEADERS = {
  // one table with every action; Update only once the row is with someone in the team
  [VIEWS.HEAD]: [
    ...pick(...studentColumns, ...tutorColumns, 'croName', 'assignedToName', 'assignedOn', 'assignRemarks', ...progressColumns),
    actions.assign,
    {
      ...actions.progress,
      isDisabled: ({ row }) => !row.assignedTo,
      toolTipGetter: ({ row }) => (row.assignedTo ? '' : 'Assign this replacement first'),
    },
    actions.history,
  ],
  [VIEWS.MINE]: [
    ...pick(...studentColumns, ...tutorColumns, 'croName', 'assignedByName', 'assignedOn', 'assignRemarks', ...progressColumns),
    actions.progress,
  ],
  // read-only: the CRO follows the TA wing's progress on their own students
  [VIEWS.CRO]: pick(...studentColumns, ...tutorColumns, 'assignedToName', ...progressColumns),
};
